import { useCallback, useState } from 'react';
import { Group, Modal, Title, ScrollArea, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { type Row } from '@tanstack/react-table';

import { CenteredLoader, Pagination, Table } from '../../components';
import { DeleteProposal, RenameProposal, FastDraftSetup } from '../../components/Modals';
import useProposalTableData from '../../hooks/useProposalTableData';
import { type ProposalRow } from '../../types/tableTypes';
import useOrgPermissions from '../../hooks/useOrgPermissions';
import TableEmptyState, { type TableEmptyStateProps } from '../../components/Table/TableEmptyState';
import { defaultModalProps } from '../../utils/mantineUtils';
import DownloadProposal from '../../components/Modals/DownloadProposal';
import { getProposalRouteFromStatus } from '../../utils/routerUtils';
import { NewProposalMenu } from '../../components/DropdownMenus';

const Proposals: React.FC = () => {
  const { organization } = useParams();
  const { userHasPermission, isLoading: permissionsLoading } = useOrgPermissions();
  const [renameProposalOpened, renameProposalHandlers] = useDisclosure();
  const [deleteProposalOpened, deleteProposalHandlers] = useDisclosure();
  const [downloadProposalOpened, downloadProposalHandlers] = useDisclosure();
  const [fastDraftSetupOpened, fastDraftSetupHandlers] = useDisclosure();
  const [currentProposalUid, setCurrentProposalUid] = useState('');
  const navigate = useNavigate();
  const onRowClick = useCallback((row: Row<ProposalRow>) => {
    const route = getProposalRouteFromStatus(
      organization ?? '',
      row.original.uid,
      row.original.status
    );
    navigate(route);
  }, [organization]);

  const handleOpenRenameProposal = (row: Row<ProposalRow>) => {
    setCurrentProposalUid(row.original.uid);
    renameProposalHandlers.open();
  };

  const handleOpenDeleteProposal = (row: Row<ProposalRow>) => {
    setCurrentProposalUid(row.original.uid);
    deleteProposalHandlers.open();
  };

  const handleOpenDownloadProposal = (row: Row<ProposalRow>) => {
    setCurrentProposalUid(row.original.uid);
    downloadProposalHandlers.open();
  };

  const handleNavigateToCreation = useCallback(() => {
    navigate('create-proposal');
  }, [navigate]);

  const { table, isLoading: tableLoading } = useProposalTableData({
    organizationSlug: organization,
    onRename: handleOpenRenameProposal,
    onDelete: handleOpenDeleteProposal,
    onDownload: handleOpenDownloadProposal
  });

  const isLoading = permissionsLoading || tableLoading;
  const userCanCreateProposal = userHasPermission('create_proposal');
  const emptyStateProps: TableEmptyStateProps = {
    text: 'You haven\'t generated any proposals yet.',
    actionButton: userHasPermission('create_proposal') && <NewProposalMenu
      isLoading={isLoading}
      onClickFastDraft={fastDraftSetupHandlers.open}
      onClickFullSetup={handleNavigateToCreation}
    />
  };

  const currentProposalName = table.getRowModel().rows.find(
    (row) => row.original.uid === currentProposalUid
  )?.original?.name;

  return (<>
    {currentProposalUid !== '' && currentProposalName !== undefined && (
      <>
        <Modal opened={renameProposalOpened} {...defaultModalProps}>
          <RenameProposal
            close={renameProposalHandlers.close}
            proposalUid={currentProposalUid}
            proposalName={currentProposalName}
          />
        </Modal>
        <Modal opened={deleteProposalOpened} {...defaultModalProps}>
          <DeleteProposal
            close={deleteProposalHandlers.close}
            proposalName={currentProposalName}
            proposalUid={currentProposalUid}
          />
        </Modal>
        <Modal opened={downloadProposalOpened} {...defaultModalProps} size={750}>
          <DownloadProposal
            onClose={downloadProposalHandlers.close}
            proposalUid={currentProposalUid}
          />
        </Modal>
      </>
    )}
    <Modal opened={fastDraftSetupOpened} {...defaultModalProps} size={1000}>
      <FastDraftSetup
        onClose={fastDraftSetupHandlers.close}
      />
    </Modal>
    <Stack gap={0} flex={1} mt={12}>
      <Stack gap={8} px={16}>
        <Group justify='space-between'>
          <Title order={6}> Proposals</Title>
          {userCanCreateProposal && (
            <NewProposalMenu
              isLoading={isLoading}
              onClickFastDraft={fastDraftSetupHandlers.open}
              onClickFullSetup={handleNavigateToCreation}
            />
          )}
        </Group>
      </Stack>
      <ScrollArea flex={1} >
        {
          isLoading
            ? <CenteredLoader style={{ height: 224 }} />
            : (
                table.getRowModel().rows.length === 0
                  ? <TableEmptyState {...emptyStateProps} />
                  : <Table table={table} onRowClick={onRowClick} />
              )
        }
      </ScrollArea>
      <Pagination
        total={table.getPageCount()}
        value={table.getState().pagination.pageIndex + 1}
        onChange={(value) => { table.setPageIndex(value - 1); }}
      />
    </Stack>
  </>
  );
};

export default Proposals;
